import { GlobalPrintStyles, Root, Header, Brand, Title, Sub, Section, SectionTitle, Table } from './calendar-print.style'
import { useCalendarSlots, useConfig, useCurrentLocation, useCurrentShop } from 'api'
import { getCustomerName, getDateRange, timezoneFormatter, TranslateFunction, useTranslate } from 'utils'
import { useMemo } from 'react'
import { CalendarSlot, CalendarSlotItem, CalendarSlotType, Resource, SimpleResourceWithSlots } from 'types'

type Props = {
    date: Date
    selectedResources: Resource[] | undefined
}

export const CalendarPrint = ({ date, selectedResources }: Props) => {

    //hooks:
    const { __ } = useTranslate()
    const { data: config } = useConfig()
    const { data: currentLocation } = useCurrentLocation()
    const { data: shop } = useCurrentShop()
    const timezone = currentLocation ? currentLocation.timezone : config ? config.settings.timezone : null

    //slots (same cache key as the live calendar -> cache hit, reuses loaded data):
    const resourceIds = useMemo(() => selectedResources?.map(resource => resource.id) ?? [], [selectedResources])
    const { data } = useCalendarSlots(resourceIds, date, timezone, resourceIds.length > 0)

    //compose resources with their slots (same pattern as calendar-page-calendar):
    const resources: SimpleResourceWithSlots[] = useMemo(() => {

        if(!data || !selectedResources){
            return []
        }

        return selectedResources.map(resource => {
            const slots = data.resources.find(mem => mem.id === resource.id)?.slots ?? []
            return {
                id: resource.id,
                type: resource.type,
                name: resource.name,
                image: resource.image,
                description: resource.description,
                status: resource.status,
                slots
            }
        })

    }, [data, selectedResources])

    //not ready yet (loading / no resources / no config): render the global styles
    //and an empty root so the rest of the app stays unaffected on screen and in print
    if(!config || !timezone || !data || !resources.length){
        return (
            <>
                <GlobalPrintStyles/>
                <Root className='calendar-print-root'/>
            </>
        )
    }

    return (
        <>
            <GlobalPrintStyles/>
            <Root className='calendar-print-root'>

                {/* header */}
                <Header>
                    {shop && <Brand>{shop.name}</Brand>}
                    <Title>{__`calendar`} — {getDateRange(date, 1, config, true)}</Title>
                    <Sub>{__`timezone`}: {timezoneFormatter(timezone)}</Sub>
                </Header>

                {/* per-resource sections */}
                {resources.map(resource => (
                    <Section key={resource.id}>

                        <SectionTitle>
                            {resource.name} ({resource.type.name})
                        </SectionTitle>

                        <Table>
                            <colgroup>
                                <col style={{ width: '22%' }}/>
                                <col style={{ width: '34%' }}/>
                                <col style={{ width: '30%' }}/>
                                <col style={{ width: '14%' }}/>
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>{__`time`}</th>
                                    <th>{__`customer`}</th>
                                    <th>{__`service`}</th>
                                    <th>{__`duration`}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buildPrintRows(resource.slots, config.locale, __).map(row => (
                                    <tr key={row.key}>
                                        <td>{row.time}</td>
                                        <td>{row.customer}</td>
                                        <td>{row.service}</td>
                                        <td>{row.duration}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                    </Section>
                ))}

            </Root>
        </>
    )

}

//findSlotType (mirrors the canonical logic in calendar-time-slot.tsx):
const findSlotType = (items: CalendarSlotItem[]): CalendarSlotType => {
    if(items.length){
        const first = items[0]
        if(first.type === CalendarSlotType.BUFFER || first.type === CalendarSlotType.EXTERNAL){
            return first.type
        }
        return items.some(item => item.type === CalendarSlotType.APPOINTMENT) ? CalendarSlotType.APPOINTMENT : CalendarSlotType.RESERVATION
    }
    return CalendarSlotType.APPOINTMENT
}

//getTimeRange (locale-aware start-end range):
const getTimeRange = (slot: CalendarSlot, locale: string) => {
    const format = new Intl.DateTimeFormat([locale, 'en-US'], { hour: 'numeric', minute: 'numeric' })
    const start = new Date(slot.dateTime)
    const end = new Date(slot.dateTime)
    end.setMinutes(end.getMinutes() + slot.duration)
    return format.formatRange(start, end)
}

//getServiceCell (uses existing translation keys + variantTitle):
const getServiceCell = (slot: CalendarSlot, __: TranslateFunction) => {
    const slotType = findSlotType(slot.items)
    if(slotType === CalendarSlotType.EXTERNAL){
        return __`synced_event`
    }
    if(slotType === CalendarSlotType.BUFFER){
        return __`buffer_time`
    }
    const base = slot.service?.title ?? `[${__`deleted_service`}]`
    return slot.variantTitle ? `${base} - ${slot.variantTitle}` : base
}

//a single printable row (one per booking for appointment/reservation slots,
//one per slot for external; buffers are omitted):
type PrintRow = {
    key: string
    time: string
    customer: string
    service: string
    duration: string
}

//buildPrintRows: expands each slot into one or more rows. Appointment and
//reservation slots produce one row per booking (so a group appointment lists
//each customer on its own line); external slots produce a single row;
//buffers are omitted (they are not bookings):
const buildPrintRows = (slots: CalendarSlot[], locale: string, __: TranslateFunction): PrintRow[] => {
    const rows: PrintRow[] = []
    slots.forEach((slot, slotIndex) => {
        const slotType = findSlotType(slot.items)
        //buffers are padding, not bookings — do not print them:
        if(slotType === CalendarSlotType.BUFFER){
            return
        }
        const time = getTimeRange(slot, locale)
        const service = getServiceCell(slot, __)
        const duration = __('duration_min', { duration: slot.duration })
        if(slotType === CalendarSlotType.EXTERNAL){
            rows.push({ key: `${slotIndex}`, time, customer: '', service, duration })
            return
        }
        slot.items.forEach((item, itemIndex) => {
            rows.push({
                key: `${slotIndex}-${itemIndex}`,
                time,
                customer: getItemCustomerCell(item, __),
                service,
                duration,
            })
        })
    })
    return rows
}

//getItemCustomerCell: appointment -> customer name (+ qty when > 1);
//reservation -> "Reserved" (+ qty when > 1); reservations carry no customer:
const getItemCustomerCell = (item: CalendarSlotItem, __: TranslateFunction) => {
    if(item.type === CalendarSlotType.APPOINTMENT){
        const name = getCustomerName(item.customer) ?? __`unknown`
        return item.quantity && item.quantity > 1 ? `${name} (${item.quantity})` : name
    }
    if(item.type === CalendarSlotType.RESERVATION){
        const reserved = __`reserved`
        return item.quantity && item.quantity > 1 ? `${reserved} (${item.quantity})` : reserved
    }
    return ''
}
